// PipelineBuilder — Enhancement #1: Configurable Assessment Pipeline.
// Recruiters build a role-specific assessment funnel per JD: add stages from
// a catalog, reorder, enable/disable, set passing score + modality.
//
// Backend:
//   GET  /api/jobs/assessment-catalog
//   GET  /api/jobs/:jobId/pipeline
//   PUT  /api/jobs/:jobId/pipeline   { pipeline: [...] }

import React, { useEffect, useState, useRef } from "react";
import {
  Plus, Trash2, ChevronUp, ChevronDown, Save, Loader2, GripVertical,
  Brain, Code2, MessageSquare, Sparkles, FileText, Gauge, ListChecks, Wand2,
  Link2, Copy, Check, Upload, RefreshCw, Edit2, PlusCircle, Send,
  CheckCircle, XCircle, Clock, Settings, Bot, ListOrdered, X as XIcon,
  ClipboardList, Users, SlidersHorizontal,
} from "lucide-react";
import AssessmentSetupDrawer from "./AssessmentSetupDrawer";
import api from "../../services/api";
import { resumeApi } from "../../services/resumeApi";
import config from "../../config/config";
import { toast } from "react-toastify";

const TYPE_ICON = {
  pre_screening: MessageSquare, final_interview: Sparkles, psychoanalytical: Brain,
  gen_ai_readiness: Bot,
  aptitude: Gauge, technical: ListChecks, coding: Code2, communication: MessageSquare,
  ai_interview: Sparkles, case_study: FileText, custom: Wand2, situational_judgement: Brain,
  questionnaire: ClipboardList, human_interview: Users,
};

const MODALITIES = ["inherit", "audio", "text"];

// Interview/agent-based types run on prompts — no material upload needed
// Only assessment types (aptitude, technical, case_study, custom) support material upload
const INTERVIEW_TYPES = new Set([
  'pre_screening', 'final_interview', 'ai_interview',
  'communication', 'psychoanalytical', 'coding',
  'gen_ai_readiness', 'situational_judgement',
]);

// These types have their own dedicated builder UI — not material upload or interview brief
const CUSTOM_BUILDER_TYPES = new Set(['questionnaire', 'human_interview']);

// Mirrors server DRIVER_FOR_TYPE — which interview engine actually runs a stage.
const ENGINE_FOR_TYPE = {
  pre_screening:    "Pre-screening engine",
  final_interview:  "Final interview engine",
  psychoanalytical: "Psychometric agent",
  coding:           "Coding agent",
  gen_ai_readiness: "Gen AI readiness agent",
  communication:    "AI interviewer",
  ai_interview:     "AI interviewer",
  technical:        "AI interviewer",
  aptitude:         "AI interviewer",
  case_study:       "AI interviewer",
  custom:           "AI interviewer",
};

let _uid = 0;
const tempId = () => `new-${Date.now()}-${_uid++}`;

const DEFAULT_EXAM_RULES = [
  'Exam runs in fullscreen — do not exit',
  'Tab switching and copy/paste are monitored',
  'Your webcam is required for identity verification',
  'Exam auto-submits when time runs out',
  'Scores and results are shared only with the hiring team',
];

const PipelineBuilder = ({ jobId, onSaved }) => {
  const [catalog, setCatalog] = useState([]);
  const [stages, setStages]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);
  // Design brief per stage
  const [designBriefs, setDesignBriefs]         = useState({}); // { [stageId]: briefObj }
  const [showBriefFor, setShowBriefFor]         = useState(null); // stageId showing brief form
  // Material upload per stage
  const [materialStage, setMaterialStage]       = useState(null); // stageId being uploaded
  const [materialStatus, setMaterialStatus]     = useState({}); // { [stageId]: 'processing'|'ready'|'failed' }
  const [materialData, setMaterialData]         = useState({}); // { [stageId]: material doc }
  const [editingQuestion, setEditingQuestion]   = useState(null); // { stageId, idx }
  const fileInputRef = useRef(null);
  // Request new assessment type
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm]           = useState({ name:'', description:'', purpose:'', evaluationMethod:'' });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [myRequests, setMyRequests]             = useState([]);
  // Exam rules editor per stage
  const [showRulesFor, setShowRulesFor] = useState(null); // stageId
  // Assessment setup drawer
  const [setupStage, setSetupStage] = useState(null); // full stage object
  // Candidate journey links
  const [candidates, setCandidates] = useState([]);
  const [selectedCand, setSelectedCand] = useState("");
  const [copiedKey, setCopiedKey] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/jobs/${jobId}/pipeline`);
        const d = r.data?.data || {};
        setCatalog(d.catalog || []);
        setStages((d.pipeline || []).map((s) => ({ ...s, _key: s.stageId || tempId() })));
      } catch (e) {
        // Fall back to the standalone catalog if the job has no pipeline yet
        try {
          const c = await api.get(`/jobs/assessment-catalog`);
          setCatalog(c.data?.data || []);
        } catch (_) { toast.error("Couldn't load assessment catalog"); }
      } finally {
        setLoading(false);
      }
    })();
  }, [jobId]);

  // Pull a few shortlisted candidates so the recruiter can grab journey links.
  useEffect(() => {
    (async () => {
      try {
        const r = await resumeApi.getAllResumes(jobId, 1, 50, "shortlisted");
        const list = r?.data?.resumes || [];
        setCandidates(list);
        if (list.length) setSelectedCand(list[0]._id);
      } catch (_) { /* non-fatal — links section just hides */ }
    })();
  }, [jobId]);

  const savedStages = stages.filter((s) => s.stageId && !String(s.stageId).startsWith("new-"));
  const linkFor = (stageId) =>
    `${config.baseUrl}/stage-interview/${jobId}/${selectedCand}/${stageId}`;
  const copyLink = async (stageId) => {
    try {
      await navigator.clipboard.writeText(linkFor(stageId));
      setCopiedKey(stageId);
      setTimeout(() => setCopiedKey(""), 1500);
    } catch (_) { toast.info("Copy failed — select the link text manually."); }
  };

  const addStage = (type) => {
    const cat = catalog.find((c) => c.type === type);
    setStages((s) => [...s, {
      _key: tempId(), stageId: tempId(), type,
      label: cat?.label || type, enabled: true, order: s.length,
      passingScore: 60, modality: "inherit", brief: "", promptKind: "",
      stagePrompt: "",
    }]);
    setDirty(true);
  };

  const update = (key, patch) => {
    setStages((s) => s.map((st) => (st._key === key ? { ...st, ...patch } : st)));
    setDirty(true);
  };
  const remove = (key) => { setStages((s) => s.filter((st) => st._key !== key)); setDirty(true); };
  const move = (i, dir) => {
    setStages((s) => {
      const j = i + dir;
      if (j < 0 || j >= s.length) return s;
      const next = [...s];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    setDirty(true);
  };

  // ── Material upload handlers ─────────────────────────────────────────────
  const handleMaterialUpload = async (stageId, stageLabel, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('material', file);
    if (designBriefs[stageId]) {
      formData.append('designBrief', JSON.stringify(designBriefs[stageId]));
    }
    setMaterialStatus(s => ({ ...s, [stageId]: 'processing' }));
    setMaterialStage(stageId);
    try {
      await api.post(`/jobs/${jobId}/pipeline-stage/${stageId}/materials`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('File uploaded — generating assessment…');
      pollMaterial(stageId);
    } catch (e) {
      toast.error('Upload failed: ' + (e.response?.data?.message || e.message));
      setMaterialStatus(s => ({ ...s, [stageId]: 'failed' }));
    }
  };

  const pollMaterial = (stageId) => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const r = await api.get(`/jobs/${jobId}/pipeline-stage/${stageId}/materials`);
        const mat = r.data?.data;
        if (mat?.status === 'ready') {
          clearInterval(interval);
          setMaterialData(d => ({ ...d, [stageId]: mat }));
          setMaterialStatus(s => ({ ...s, [stageId]: 'ready' }));
          toast.success('Assessment generated successfully!');
        } else if (mat?.status === 'failed' || attempts > 30) {
          clearInterval(interval);
          setMaterialStatus(s => ({ ...s, [stageId]: 'failed' }));
          toast.error(mat?.errorMessage || 'Generation failed');
        }
      } catch { if (attempts > 30) clearInterval(interval); }
    }, 3000);
  };

  const fetchMaterial = async (stageId) => {
    try {
      const r = await api.get(`/jobs/${jobId}/pipeline-stage/${stageId}/materials`);
      const mat = r.data?.data;
      if (mat) {
        setMaterialData(d => ({ ...d, [stageId]: mat }));
        setMaterialStatus(s => ({ ...s, [stageId]: mat.status }));
        // Pre-populate candidateInstructions and examRules from saved material
        setDesignBriefs(b => ({
          ...b,
          [stageId]: {
            ...(b[stageId] || {}),
            ...(mat.candidateInstructions ? { candidateInstructions: mat.candidateInstructions } : {}),
            examRules: mat.examRules?.length ? mat.examRules : [...DEFAULT_EXAM_RULES],
          },
        }));
      }
    } catch (_) {}
  };

  const saveQuestions = async (stageId) => {
    const mat = materialData[stageId];
    if (!mat) return;
    try {
      const r = await api.put(`/jobs/${jobId}/pipeline-stage/${stageId}/materials/${mat._id}`, {
        generatedQuestions:    mat.generatedQuestions,
        scoringLogic:          mat.scoringLogic,
        assessmentStructure:   mat.assessmentStructure,
        candidateInstructions: designBriefs[stageId]?.candidateInstructions || mat.candidateInstructions || '',
        examRules:             designBriefs[stageId]?.examRules || mat.examRules || DEFAULT_EXAM_RULES,
      });
      setMaterialData(d => ({ ...d, [stageId]: r.data?.data }));
      setEditingQuestion(null);
      toast.success('Assessment saved');
    } catch { toast.error('Save failed'); }
  };

  const updateQuestion = (stageId, idx, patch) => {
    setMaterialData(d => {
      const mat = { ...d[stageId] };
      const qs  = [...(mat.generatedQuestions || [])];
      qs[idx]   = { ...qs[idx], ...patch };
      mat.generatedQuestions = qs;
      return { ...d, [stageId]: mat };
    });
  };

  const removeQuestion = (stageId, idx) => {
    setMaterialData(d => {
      const mat = { ...d[stageId] };
      const qs  = [...(mat.generatedQuestions || [])];
      qs.splice(idx, 1);
      mat.generatedQuestions = qs;
      return { ...d, [stageId]: mat };
    });
  };

  // ── Request new type handlers ────────────────────────────────────────────
  const fetchMyRequests = async () => {
    try {
      const r = await api.get('/jobs/assessment-requests');
      setMyRequests(r.data?.data || []);
    } catch (_) {}
  };

  const submitRequest = async () => {
    const { name, description, purpose, evaluationMethod } = requestForm;
    if (!name || !description || !purpose || !evaluationMethod) {
      toast.error('All fields are required'); return;
    }
    try {
      setSubmittingRequest(true);
      await api.post('/jobs/assessment-requests', requestForm);
      toast.success('Request submitted — admin will review shortly');
      setShowRequestModal(false);
      setRequestForm({ name:'', description:'', purpose:'', evaluationMethod:'' });
      fetchMyRequests();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Submission failed');
    } finally { setSubmittingRequest(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        pipeline: stages.map((s, i) => ({
          stageId: s.stageId?.startsWith("new-") ? undefined : s.stageId,
          type: s.type, label: s.label, enabled: s.enabled, order: i,
          passingScore: Number(s.passingScore) || 60, modality: s.modality,
          brief: s.brief || "", promptKind: s.promptKind || "",
          stagePrompt: s.stagePrompt || "",
        })),
      };
      const r = await api.put(`/jobs/${jobId}/pipeline`, payload);
      const saved = r.data?.data?.pipeline || [];
      setStages(saved.map((s) => ({ ...s, _key: s.stageId })));
      setDirty(false);
      toast.success("Pipeline saved");
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-10 flex items-center gap-2 text-meraki-indigo/60 font-outfit"><Loader2 className="w-5 h-5 animate-spin" /> Loading pipeline…</div>;
  }

  return (
    <>
    <div className="p-6 font-outfit max-w-4xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="title-meraki text-xl">Assessment Pipeline</h2>
          <p className="subtitle-meraki text-sm mt-1">
            Build a role-specific funnel for this job. Drag-order, enable/disable, and set a pass bar per stage.
            {stages.length === 0 && " No stages added yet — add assessments from the catalog below."}
          </p>
        </div>
        <button onClick={save} disabled={saving || !dirty} className="btn-meraki text-sm disabled:opacity-40">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save pipeline"}
        </button>
      </div>

      {/* Stage list */}
      <div className="space-y-3 mb-6">
        {stages.length === 0 && (
          <div className="card-meraki p-8 text-center text-meraki-graphite/50 text-sm">
            No stages yet. Add assessments from the catalog below.
          </div>
        )}
        {stages.map((s, i) => {
          const Icon = TYPE_ICON[s.type] || Sparkles;
          return (
            <div key={s._key} className={`card-meraki p-4 ${!s.enabled ? "opacity-60" : ""}`}>
              <div className="flex items-center gap-3">
                {/* Order controls */}
                <div className="flex flex-col text-meraki-graphite/30">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="hover:text-meraki-indigo disabled:opacity-20"><ChevronUp className="w-4 h-4" /></button>
                  <GripVertical className="w-4 h-4" />
                  <button onClick={() => move(i, +1)} disabled={i === stages.length - 1} className="hover:text-meraki-indigo disabled:opacity-20"><ChevronDown className="w-4 h-4" /></button>
                </div>

                <div className="w-10 h-10 rounded-meraki bg-meraki-soft-blue flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-meraki-indigo" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-meraki-indigo/50 w-5">{i + 1}</span>
                    <input
                      value={s.label}
                      onChange={(e) => update(s._key, { label: e.target.value })}
                      className="font-semibold text-meraki-graphite bg-transparent border-b border-transparent hover:border-meraki-soft-blue focus:border-meraki-indigo focus:outline-none flex-1"
                    />
                  </div>
                  <div className="text-xs text-meraki-graphite/40 ml-7 flex items-center gap-1.5">
                    <span className="capitalize">{s.type.replace(/_/g, " ")}</span>
                    <span className="text-meraki-graphite/25">•</span>
                    {s.type === 'questionnaire'
                      ? <span className="text-blue-500 font-medium">HR Questionnaire</span>
                      : s.type === 'human_interview'
                      ? <span className="text-purple-500 font-medium">Human Interview (Google Meet)</span>
                      : INTERVIEW_TYPES.has(s.type)
                      ? <span className="text-meraki-indigo/50">AI Interview</span>
                      : <span className="text-orange-400 font-medium">Q&amp;A Exam (upload material)</span>
                    }
                  </div>
                </div>

                {/* Pass score */}
                <div className="text-center">
                  <input type="number" min={0} max={100} value={s.passingScore}
                    onChange={(e) => update(s._key, { passingScore: e.target.value })}
                    className="w-16 p-1.5 border border-meraki-soft-blue rounded-meraki text-center text-sm" />
                  <div className="text-[10px] text-meraki-graphite/40 mt-0.5">pass %</div>
                </div>

                {/* Modality */}
                <select value={s.modality} onChange={(e) => update(s._key, { modality: e.target.value })}
                  className="p-1.5 border border-meraki-soft-blue rounded-meraki text-xs capitalize">
                  {MODALITIES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>

                {/* Enable toggle */}
                <button onClick={() => update(s._key, { enabled: !s.enabled })}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold ${s.enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {s.enabled ? "On" : "Off"}
                </button>

                <button onClick={() => remove(s._key)} className="text-meraki-graphite/30 hover:text-meraki-coral"><Trash2 className="w-4 h-4" /></button>
              </div>


              {/* ── Human interview info ── */}
              {s.type === 'human_interview' && (
                <div className="mt-2 ml-12 bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-xs text-purple-700 font-medium">Human Interview stage</p>
                  <p className="text-xs text-purple-500 mt-1">After saving the pipeline, you can assign candidates to the AI or Human track from the candidate list.</p>
                </div>
              )}

              {/* ── Configure button for assessment + questionnaire stages ── */}
              {(s.type === 'questionnaire' || (!INTERVIEW_TYPES.has(s.type) && !CUSTOM_BUILDER_TYPES.has(s.type))) && (
                <div className="mt-2 ml-12">
                  {s.stageId?.startsWith('new-') ? (
                    <p className="text-xs text-amber-500 flex items-center gap-1">
                      ⚠ Save the pipeline first to configure this assessment
                    </p>
                  ) : (
                    <button
                      onClick={() => setSetupStage(s)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-meraki-soft-blue text-meraki-indigo rounded-lg text-xs font-semibold hover:bg-meraki-indigo hover:text-white transition-colors"
                    >
                      <SlidersHorizontal className="w-3.5 h-3.5" />
                      Configure assessment
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Catalog */}
      <div className="card-meraki p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold uppercase tracking-wide text-meraki-graphite/60">Add assessment</h3>
          <button onClick={() => { setShowRequestModal(true); fetchMyRequests(); }}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-meraki-coral text-meraki-coral rounded-lg text-xs font-medium hover:bg-meraki-coral-50 transition-colors">
            <PlusCircle className="w-3.5 h-3.5" />
            Request New Type
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {catalog.map((c) => {
            const Icon = TYPE_ICON[c.type] || Sparkles;
            return (
              <button key={c.type} onClick={() => addStage(c.type)}
                className={`flex flex-col items-start gap-2 p-3 rounded-meraki border-2 transition text-left
                  ${c.isCustom ? 'border-orange-300 bg-orange-50 hover:border-orange-500' : 'border-meraki-soft-blue hover:border-meraki-coral hover:bg-meraki-coral-50'}`}>
                <div className="flex items-center gap-2 w-full">
                  <Icon className="w-4 h-4 text-meraki-indigo" />
                  {c.isCustom && <span className="text-[9px] bg-orange-200 text-orange-700 px-1 rounded font-bold ml-0.5">CUSTOM</span>}
                  <Plus className="w-3.5 h-3.5 text-meraki-coral ml-auto" />
                </div>
                <span className="font-semibold text-sm text-meraki-graphite">{c.label}</span>
                <span className="text-[11px] text-meraki-graphite/50 leading-tight">{c.desc}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Candidate journey links — share a stage directly with a candidate */}
      {savedStages.length > 0 && (
        <div className="card-meraki p-5 mt-6">
          <div className="flex items-center gap-2 mb-1">
            <Link2 className="w-4 h-4 text-meraki-indigo" />
            <h3 className="text-sm font-bold uppercase tracking-wide text-meraki-graphite/60">Share with candidates</h3>
          </div>
          <p className="text-xs text-meraki-graphite/50 mb-4">
            Each saved stage has a direct candidate link. Pick a candidate, then copy the link for the stage you want them to take.
            {dirty && " Save your changes first to get links for new stages."}
          </p>

          {candidates.length === 0 ? (
            <div className="text-sm text-meraki-graphite/50">No shortlisted candidates yet for this role.</div>
          ) : (
            <>
              <select
                value={selectedCand}
                onChange={(e) => setSelectedCand(e.target.value)}
                className="mb-4 p-2 border border-meraki-soft-blue rounded-meraki text-sm w-full md:w-80"
              >
                {candidates.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.candidateName || c.email || c._id}
                  </option>
                ))}
              </select>

              <div className="space-y-2">
                {savedStages.map((s, i) => (
                  <div key={s.stageId} className="flex items-center gap-3 p-2.5 rounded-meraki bg-meraki-soft-blue/40">
                    <span className="text-xs font-bold text-meraki-indigo/50 w-5">{i + 1}</span>
                    <span className="font-semibold text-sm text-meraki-graphite w-44 truncate">{s.label}</span>
                    <input
                      readOnly
                      value={selectedCand ? linkFor(s.stageId) : "Select a candidate…"}
                      onFocus={(e) => e.target.select()}
                      className="flex-1 text-xs text-meraki-graphite/60 bg-white border border-meraki-soft-blue rounded-meraki px-2 py-1.5 font-mono truncate"
                    />
                    <button
                      onClick={() => copyLink(s.stageId)}
                      disabled={!selectedCand}
                      className="btn-meraki-ghost text-xs flex items-center gap-1 disabled:opacity-40"
                    >
                      {copiedKey === s.stageId ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                      {copiedKey === s.stageId ? "Copied" : "Copy"}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
      {/* ── Request New Assessment Type Modal ── */}
      {showRequestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/40" onClick={() => setShowRequestModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6 z-10">
            <h3 className="text-lg font-bold text-gray-800 mb-1">Request New Assessment Type</h3>
            <p className="text-sm text-gray-500 mb-4">Submit a request to your admin. Approved types will appear in your catalog exclusively.</p>

            <div className="space-y-3">
              {[
                { field:'name', label:'Assessment Name', placeholder:'e.g. Domain Knowledge Test' },
                { field:'description', label:'Description', placeholder:'What does this assessment evaluate?' },
                { field:'purpose', label:'Purpose', placeholder:'Why is this needed for this role?' },
                { field:'evaluationMethod', label:'Evaluation Method', placeholder:'How will candidates be evaluated?' },
              ].map(({ field, label, placeholder }) => (
                <div key={field}>
                  <label className="text-xs font-semibold text-gray-600">{label} <span className="text-red-400">*</span></label>
                  <textarea value={requestForm[field]} rows={field === 'name' ? 1 : 2}
                    onChange={e => setRequestForm(f => ({ ...f, [field]: e.target.value }))}
                    placeholder={placeholder}
                    className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none" />
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowRequestModal(false)} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={submitRequest} disabled={submittingRequest}
                className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50">
                {submittingRequest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submittingRequest ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>

            {/* My past requests */}
            {myRequests.length > 0 && (
              <div className="mt-5 border-t pt-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">My Requests</h4>
                <div className="space-y-1.5 max-h-36 overflow-y-auto">
                  {myRequests.map(r => {
                    const StatusIcon = { pending: Clock, approved: CheckCircle, rejected: XCircle }[r.status];
                    const cls = { pending:'text-yellow-600', approved:'text-green-600', rejected:'text-red-500' }[r.status];
                    return (
                      <div key={r._id} className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-2">
                        <span className="font-medium text-gray-700">{r.name}</span>
                        <span className={`flex items-center gap-1 font-medium ${cls}`}>
                          <StatusIcon className="w-3 h-3" />
                          {r.status}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>

    {/* Assessment Setup Drawer */}
    {setupStage && (
      <AssessmentSetupDrawer
        jobId={jobId}
        stage={setupStage}
        onClose={() => setSetupStage(null)}
        onSaved={() => setSetupStage(null)}
      />
    )}
    </>
  );
};

export default PipelineBuilder;
